import React from "react";
import { Clock, Code2, Layers } from "lucide-react";
import { AppBadge, AppCard, AppCardContent, Radio } from "../../ui";
import Shimmer from "../../Common/Shimmer";
import { TEMPLATE_GROUPS, letterOf } from "./builderRules";

const minutesOf = (t) => (t.timing === "sections"
  ? t.sections.reduce((sum, s) => sum + (s.minutes || 0), 0)
  : t.timeLimit || 0);

function TemplateLabel({ template }) {
  return (
    <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      <span className="tw-font-semibold">{template.title}</span>
      {template.badge && <AppBadge size="sm" color="primary">{template.badge}</AppBadge>}
    </span>
  );
}

function TemplateDetail({ template }) {
  const minutes = minutesOf(template);
  const sectioned = template.sections.length > 1;
  return (
    <span className="tw-flex tw-flex-col tw-gap-2">
      <span>{template.blurb}</span>
      {sectioned && (
        <span className="tw-flex tw-flex-wrap tw-gap-1.5">
          {template.sections.map((s, i) => (
            <span
              key={s.title}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface tw-px-1.5 tw-py-0.5 tw-text-[0.6875rem] tw-text-content-muted"
            >
              {s.intent === "coding" && <Code2 size={11} aria-hidden="true" />}
              {letterOf(i)}. {s.title}
            </span>
          ))}
        </span>
      )}
      <span className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1 tw-text-content-subtle">
        <span className="tw-inline-flex tw-items-center tw-gap-1">
          <Layers size={12} aria-hidden="true" />
          {sectioned ? `${template.sections.length} sections` : "One part"}
        </span>
        <span className="tw-inline-flex tw-items-center tw-gap-1">
          <Clock size={12} aria-hidden="true" />
          {minutes ? `${minutes} min${template.timing === "sections" ? ", timed per section" : ""}` : "No time limit"}
        </span>
      </span>
    </span>
  );
}

/**
 * Step 1 — the format. One radio group across every card, so the arrow keys
 * move between formats the way they move between options in a form.
 *
 * `templates` are the formats as this institute sees them (builderRules
 * .templatesFor); they wait for the server's answer on Coding Practice, so a
 * format never shows a coding section and then loses it.
 */
export default function FormatStep({ value, onChange, templates, loading }) {
  if (loading) {
    return (
      <AppCard aria-busy="true" aria-label="Loading formats">
        <AppCardContent className="tw-grid tw-gap-3 min-[768px]:tw-grid-cols-2">
          {[0, 1, 2, 3].map((i) => <Shimmer key={i} height={132} />)}
        </AppCardContent>
      </AppCard>
    );
  }
  return (
    <AppCard>
      <AppCardContent>
        <fieldset className="tw-m-0 tw-flex tw-min-w-0 tw-flex-col tw-gap-6 tw-border-0 tw-p-0">
          <legend className="tw-sr-only">What kind of assessment is this?</legend>
          {TEMPLATE_GROUPS.map((g) => {
            const items = templates.filter((t) => t.group === g.key);
            return (
              <div key={g.key} className="tw-flex tw-flex-col tw-gap-2.5">
                <h3 className="tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
                  {g.title}
                </h3>
                <div className="tw-grid tw-gap-3 min-[768px]:tw-grid-cols-2">
                  {items.map((t) => (
                    <Radio
                      key={t.key}
                      variant="card"
                      name="assessment-format"
                      value={t.key}
                      checked={value === t.key}
                      onChange={() => onChange(t.key)}
                      label={<TemplateLabel template={t} />}
                      description={<TemplateDetail template={t} />}
                      className="tw-h-full"
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </fieldset>
      </AppCardContent>
    </AppCard>
  );
}
